import * as THREE from "three";
import { config } from "../config.js";
import { stageAt } from "../sim/stage-rules.js";
import { fieldOf } from "../sim/scale.js";

// 画面に重ねる2Dの表示(見た目だけ。2026-10-30 段階1「気持ちいい」)。
// - ダメージの数字: 敵に当たった位置から浮かび上がる。同じ敵への続けざまのダメージは1つの数字にまとめる。とどめは金色
// - 被弾の向き: 当たった物の来た向きに、自機のまわりの赤い弧と、画面の端の赤みを出す
// - 熱の帯(大気圏の仕掛け): 自機が熱い端に近づくと、その側の画面の端を橙色に光らせる
// - SHIELD のバー(2026-10-30 オーナー要望「画面の上だと遠くて見づらい」): 自機の下にぶら下げる。主観の視点では画面の下の決まった位置(CSS の .cockpit)
// 3D の位置はカメラで画面の位置に直して描く。world には書き戻さない。

const J = config.juice;

export function createOverlay() {
  const canvas = document.createElement("canvas");
  canvas.id = "overlay";
  document.body.insertBefore(canvas, document.getElementById("flash"));
  const ctx = canvas.getContext("2d");
  let w = 0;
  let h = 0;
  let dpr = 1;
  function resize() {
    dpr = Math.min(2, window.devicePixelRatio || 1);
    w = window.innerWidth;
    h = window.innerHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
  }
  window.addEventListener("resize", resize);
  resize();

  const nums = []; // { id, x, y, z, v, t }
  const hurts = []; // { x, y, z, t }
  const v = new THREE.Vector3();

  function onEvents(events) {
    for (const e of events) {
      if (e.k === "dmg") {
        if (e.z < J.damageFromZ) continue;
        // 同じ敵への続けざまの当たりは、まだ若い数字に足す
        const n = nums.find((o) => o.id === e.id && o.t < J.damageMergeSeconds);
        if (n) {
          n.v += e.v;
          n.last = n.last || e.last;
          n.x = e.x;
          n.y = e.y;
          n.z = e.z;
        } else {
          if (nums.length >= J.damageMax) nums.shift();
          nums.push({ id: e.id, x: e.x, y: e.y, z: e.z, v: e.v, last: e.last, t: 0 });
        }
      } else if (e.k === "hurt" && e.fx !== undefined) {
        hurts.push({ x: e.fx, y: e.fy, z: e.fz, t: 0 });
        if (hurts.length > 4) hurts.shift();
      }
    }
  }

  // 3D の位置 → 画面の位置(カメラの後ろなら null)
  function toScreen(camera, x, y, z) {
    v.set(x, y, z).project(camera);
    if (v.z > 1) return null;
    return [((v.x + 1) / 2) * w, ((1 - v.y) / 2) * h];
  }

  const shipShield = document.getElementById("ship-shield");
  function placeShield(world, camera, cockpit) {
    if (!shipShield) return;
    shipShield.classList.toggle("cockpit", cockpit);
    if (cockpit) {
      shipShield.style.transform = "";
      return;
    }
    const p = world.player;
    const s = toScreen(camera, p.x, p.y - config.graphics.shieldBarBelow, 0);
    if (s) shipShield.style.transform = `translate(${s[0].toFixed(1)}px, ${s[1].toFixed(1)}px) translate(-50%, 0)`;
  }

  // cockpit: 主観の視点か(自機を描かない)
  function draw(world, camera, dtReal, cockpit = false) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    placeShield(world, camera, cockpit);

    // ダメージの数字
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    for (let i = nums.length - 1; i >= 0; i--) {
      const n = nums[i];
      n.t += dtReal;
      if (n.t > J.damageSeconds) {
        nums.splice(i, 1);
        continue;
      }
      const s = toScreen(camera, n.x, n.y, n.z);
      if (!s) continue;
      const k = n.t / J.damageSeconds;
      const big = n.last; // とどめの一撃は金色で大きく
      const pop = k < 0.15 ? 1 + (0.15 - k) * 4 : 1; // 出た瞬間だけ大きく
      const size = (big ? 22 : 15) * pop;
      ctx.font = `700 ${size}px Oxanium, sans-serif`;
      ctx.globalAlpha = 1 - k * k;
      const y = s[1] - 14 - k * 34;
      const text = String(Math.round(n.v));
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(0,0,0,0.7)";
      ctx.strokeText(text, s[0], y);
      ctx.fillStyle = big ? "#ffd23a" : "#ffffff";
      ctx.fillText(text, s[0], y);
    }
    ctx.globalAlpha = 1;

    const p = world.player;
    drawHeat(world, camera, p);

    // 被弾の向き
    const ps = toScreen(camera, p.x, p.y, 0);
    for (let i = hurts.length - 1; i >= 0; i--) {
      const hu = hurts[i];
      hu.t += dtReal;
      if (hu.t > J.hurtSeconds || !ps) {
        hurts.splice(i, 1);
        continue;
      }
      const k = 1 - hu.t / J.hurtSeconds;
      // 当たった物の位置を、自機の面で見た向きにする(真正面からの弾でも向きが出るように、画面の位置ではなく x・y の差を使う)
      const a = Math.atan2(-(hu.y - p.y), hu.x - p.x);
      const strong = Math.hypot(hu.x - p.x, hu.y - p.y) > 6;
      const r = 70;
      ctx.lineWidth = 9;
      ctx.lineCap = "round";
      ctx.strokeStyle = `rgba(255,60,60,${0.9 * k})`;
      ctx.beginPath();
      if (strong) ctx.arc(ps[0], ps[1], r, a - 0.5, a + 0.5);
      else ctx.arc(ps[0], ps[1], r, 0, Math.PI * 2); // 真正面からなら輪ごと
      ctx.stroke();
      if (strong) {
        // 画面の端の、来た向きの側を赤くする
        const ex = w / 2 + Math.cos(a) * w * 0.5;
        const ey = h / 2 + Math.sin(a) * h * 0.5;
        const g = ctx.createRadialGradient(ex, ey, 0, ex, ey, Math.max(w, h) * 0.45);
        g.addColorStop(0, `rgba(255,30,30,${0.45 * k})`);
        g.addColorStop(1, "rgba(255,30,30,0)");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }
    }
  }

  // 端に近いほど、その側の画面の端を橙色に光らせる(0=遠い〜1=熱い所の中)
  function drawHeat(world, camera, p) {
    if (world.hold || stageAt(world.progress).gimmick !== "heat") return;
    const f = fieldOf(world);
    const e = config.gimmicks.heat.edge;
    const flick = 0.85 + Math.sin(performance.now() / 90) * 0.15;
    const near = (v, half) => Math.max(0, Math.min(1, (Math.abs(v) / half - (e - 0.2)) / 0.2));
    const sides = [
      [p.x < 0 ? near(p.x, f.halfWidth) : 0, 0, 0, w * 0.3, 0],
      [p.x > 0 ? near(p.x, f.halfWidth) : 0, w, 0, w * 0.7, 0],
      [p.y > 0 ? near(p.y, f.halfHeight) : 0, 0, 0, 0, h * 0.3],
      [p.y < 0 ? near(p.y, f.halfHeight) : 0, 0, h, 0, h * 0.7],
    ];
    for (const [k, x0, y0, x1, y1] of sides) {
      if (k <= 0) continue;
      const g = ctx.createLinearGradient(x0, y0, x1 || x0, y1 || y0);
      g.addColorStop(0, `rgba(255,110,20,${0.55 * k * flick})`);
      g.addColorStop(1, "rgba(255,110,20,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    }
  }

  function clear() {
    nums.length = 0;
    hurts.length = 0;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  return { onEvents, draw, clear, canvas };
}
